import {maskEmail} from 'common/HelperFunctions'
import {useEffect, useRef, useState} from 'react'
import {Form} from 'react-bootstrap'
import {toast} from 'react-toastify'

type Props = {
  email: string
  verifyLoading?: boolean
  resendLoading?: boolean
  onDarkBg?: boolean
  onSubmit: (otp: string) => void
  onResend: () => void
  onBack: () => void
}

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SEC = 60

/**
 * The `*` glyph sits near the top of the line box, so a masked email reads as if the
 * asterisks float above the surrounding characters. Nudge only the asterisk runs down
 * so they sit on the same visual line as the rest of the address.
 */
function MaskedEmail({email}: {email: string}) {
  const parts = maskEmail(email).split(/(\*+)/)
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('*') ? (
          <span key={index} style={{display: 'inline-block', transform: 'translateY(0.18em)'}}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  )
}

export function ForgotSetPasswordOtp({
  email,
  verifyLoading = false,
  resendLoading = false,
  onDarkBg = false,
  onSubmit,
  onResend,
  onBack,
}: Props) {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(OTP_LENGTH).fill(null))
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SEC)
  const [resendDisabled, setResendDisabled] = useState(true)
  const inputsDisabled = verifyLoading || resendLoading

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
    setResendDisabled(false)
  }, [countdown])

  const submitIfComplete = (digits: string[]) => {
    if (digits.every((d) => d !== '') && !verifyLoading) {
      inputRefs.current.forEach((input) => input?.blur())
      onSubmit(digits.join(''))
      return true
    }
    return false
  }

  const focusInput = (index: number) => {
    requestAnimationFrame(() => {
      inputRefs.current[index]?.focus()
      inputRefs.current[index]?.select()
    })
  }

  const fillFrom = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '')
    if (!cleaned) return
    const next = [...otp]
    const chars = cleaned.slice(0, OTP_LENGTH - index).split('')
    chars.forEach((ch, i) => {
      next[index + i] = ch
    })
    setOtp(next)
    if (!submitIfComplete(next)) {
      focusInput(Math.min(index + chars.length, OTP_LENGTH - 1))
    }
  }

  const handleChange = (index: number, value: string) => {
    fillFrom(index, value)
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const next = [...otp]
      if (next[index]) {
        next[index] = ''
        setOtp(next)
      } else if (index > 0) {
        next[index - 1] = ''
        setOtp(next)
        focusInput(index - 1)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      focusInput(index - 1)
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault()
      focusInput(index + 1)
    }
  }

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault()
    fillFrom(index, e.clipboardData.getData('text'))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (otp.every((digit) => digit !== '')) {
      onSubmit(otp.join(''))
    } else {
      toast.error('Enter the full 6-digit verification code')
    }
  }

  const handleResend = () => {
    setResendDisabled(true)
    setCountdown(RESEND_COOLDOWN_SEC)
    setOtp(Array(OTP_LENGTH).fill(''))
    onResend()
  }

  return (
    <div>
      <div className='text-center mb-8'>
        <p className={`fw-semibold fs-6 mb-2 ${onDarkBg ? 'text-white-50' : 'text-gray-500'}`}>
          Enter the 6-digit verification code sent to
        </p>
        <p className={`fw-bold fs-5 mb-0 ${onDarkBg ? 'text-white' : 'text-dark'}`}>
          <MaskedEmail email={email} />
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className='d-flex w-100 mb-6' style={{gap: 8}}>
          {otp.map((digit, index) => (
            <Form.Control
              key={index}
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              maxLength={1}
              value={digit}
              disabled={inputsDisabled}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              onFocus={(e) => e.target.select()}
              ref={(ref) => {
                inputRefs.current[index] = ref as HTMLInputElement | null
              }}
              className='text-center fw-bold p-0'
              style={{
                flex: 1,
                minWidth: 0,
                aspectRatio: '1',
                fontSize: '1.75rem',
                borderRadius: 8,
              }}
            />
          ))}
        </div>

        <button
          type='submit'
          className='btn btn-lg btn-primary fw-bolder w-100 mb-4 rounded'
          disabled={verifyLoading || resendLoading || otp.some((d) => !d)}
        >
          {verifyLoading ? 'Verifying…' : 'Verify Code'}
        </button>

        <span
          role='button'
          tabIndex={resendDisabled || resendLoading ? -1 : 0}
          onClick={resendDisabled || resendLoading ? undefined : handleResend}
          onKeyDown={(e) => {
            if (!resendDisabled && !resendLoading && (e.key === 'Enter' || e.key === ' ')) {
              handleResend()
            }
          }}
          className={`fw-bold d-block text-center mb-4 ${
            resendDisabled || resendLoading
              ? onDarkBg
                ? 'text-white-50'
                : 'text-muted'
              : 'text-primary cursor-pointer'
          }`}
          style={{pointerEvents: resendDisabled || resendLoading ? 'none' : 'auto'}}
        >
          {resendLoading
            ? 'Sending new code…'
            : resendDisabled
              ? `Request New Code (${countdown}s)`
              : 'Request New Code'}
        </span>

        <button
          type='button'
          className='btn btn-light w-100 rounded'
          disabled={verifyLoading || resendLoading}
          onClick={onBack}
        >
          Back
        </button>
      </Form>
    </div>
  )
}
